import { useRouter } from 'next/router';

import { useOAIInfiniteQuery, useOAIQuery } from '~/hooks/reactQuery';
import type { OAIParameters, OAISchema } from '~/types/openapi';

type ProductTotalCountOptions = {
  enabled?: boolean;
  variables?: OAIParameters<
    '/api/market-place/v1/search/products/count',
    'get'
  >;
};

const useProductTotalCount = (options?: ProductTotalCountOptions) => {
  const { data: totalCount } = useOAIQuery({
    queryKey: '/api/market-place/v1/search/products/count',
    variables: options?.variables,
    queryOptions: {
      ...options,
      suspense: false,
      keepPreviousData: true,
    },
  });

  return totalCount;
};

export const useSearchProducts = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const router = useRouter();
  const variables: OAIParameters<
    '/api/market-place/v1/search/products',
    'get'
  > = {
    ...router.query,
    productName: router.query.searchText as string,
  };

  const totalCount = useProductTotalCount({
    enabled,
    variables,
  });

  const infiniteQuery = useOAIInfiniteQuery<
    '/api/market-place/v1/search/products',
    OAISchema['MarketSelectProductResponseV2']
  >({
    queryKey: '/api/market-place/v1/search/products',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    variables: {
      ...variables,
    },
    queryOptions: {
      keepPreviousData: true,
      enabled,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      getNextPageParam: ({ hasNextPage, lastId, searchAfter, sortType }) => {
        if (!hasNextPage) {
          return undefined;
        }

        return { lastId, searchAfter, sortType };
      },
    },
  });

  return {
    total: totalCount || 0,
    ...infiniteQuery,
  };
};
